import { formatDistanceToNow } from 'date-fns';
import { Clock, Loader2, MessageSquare } from 'lucide-react';
import * as React from 'react';

import { TimelineEventRouter } from '@/components/tasks/task-detail/timeline/TimelineEventRouter';
import { useGetStaffNotesQuery } from '@/services/api/staffNotesService';
import { useTaskActivityListQuery } from '@/services/api/taskActivityService';
import type { StaffNote } from '@/services/types/staff-notes-types';

interface TaskActivityFeedProps {
  matterId: string;
  taskId: string;
  /** Maximum number of items to show */
  limit?: number;
  /** Show a loading state */
  isLoading?: boolean;
}

// Unified feed item type
type FeedItem =
  | { type: 'event'; id: string; timestamp: Date; data: any }
  | { type: 'note'; id: string; timestamp: Date; data: StaffNote };

// Staff note item component
const StaffNoteItem: React.FC<{ note: StaffNote }> = ({ note }) => {
  const userName = note.user
    ? `${note.user.first_name} ${note.user.last_name}`.trim() ||
      note.user.username
    : 'Unknown';

  const timeAgo = formatDistanceToNow(new Date(note.created_at), {
    addSuffix: true,
  });

  return (
    <div className="flex gap-x-2 py-2">
      <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-none">
        <MessageSquare className="h-3 w-3 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-x-1.5">
          <span className="text-xs font-medium text-zinc-900">{userName}</span>
          <span className="text-[10px] text-zinc-400">{timeAgo}</span>
        </div>
        <div className="mt-1 text-xs text-zinc-700 whitespace-pre-wrap break-words line-clamp-3">
          {note.content}
        </div>
      </div>
    </div>
  );
};

/**
 * Displays task activity/updates in a feed format.
 * Includes both domain events (task activity) and staff notes.
 * Used in the LOA Chaser widget to show related activity.
 */
export const TaskActivityFeed: React.FC<TaskActivityFeedProps> = ({
  matterId,
  taskId,
  limit = 10,
  isLoading: externalLoading,
}) => {
  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useTaskActivityListQuery({ matterId, taskId });

  const {
    data: staffNotes,
    isLoading: notesLoading,
    error: notesError,
  } = useGetStaffNotesQuery({ matterId });

  const loading = activitiesLoading || notesLoading || externalLoading;
  const error = activitiesError || notesError;

  // Combine and sort activities and notes by timestamp
  const combinedFeed = React.useMemo(() => {
    const items: FeedItem[] = [];

    // Add domain events
    if (activities) {
      activities.forEach((activity) => {
        items.push({
          type: 'event',
          id: activity.event.id ?? `event-${activity.event.occurred_at}`,
          timestamp: activity.event.occurred_at
            ? new Date(activity.event.occurred_at)
            : new Date(0),
          data: activity,
        });
      });
    }

    // Add staff notes
    if (staffNotes) {
      staffNotes.forEach((note) => {
        items.push({
          type: 'note',
          id: note.id,
          timestamp: new Date(note.created_at),
          data: note,
        });
      });
    }

    // Sort by timestamp descending (newest first) and limit
    return items
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }, [activities, staffNotes, limit]);

  if (error) {
    return (
      <div className="text-xs text-red-600 py-2 px-3">
        Failed to load activity
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (combinedFeed.length === 0) {
    return (
      <div className="py-6 flex flex-col items-center justify-center text-zinc-400">
        <Clock className="h-5 w-5 mb-1.5 text-zinc-300" />
        <p className="text-xs font-medium">No activity yet</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-zinc-100">
      {combinedFeed.map((item) => (
        <div key={`${item.type}-${item.id}`} className="py-2">
          {item.type === 'event' ? (
            <TimelineEventRouter activityItem={item.data} matterId={matterId} />
          ) : (
            <StaffNoteItem note={item.data} />
          )}
        </div>
      ))}
    </div>
  );
};

export default TaskActivityFeed;
